import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { UserEnquiryContextValues } from 'models/UserEnquiryContext';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import CurrentUserEnquiryContextContext from 'providers/CurrentUserEnquiryContext/CurrentUserEnquiryContext.context';
import UserEnquiryForm from '../UserEnquiryForm';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './AddEnquiryContextModal.styles.scss';

type AddEnquiryContextModalProps = {
  className?: string;
} & ModalProps;

const AddEnquiryContextModal: React.ForwardRefRenderFunction<
  ModalRef,
  AddEnquiryContextModalProps
> = (props, ref) => {
  const { className, ...rest } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const { updatePreferences } = useContext(CurrentUserContext);
  const { onNewEnquiryContext } = useContext(CurrentUserEnquiryContextContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('add-enquiry-context-modal', className);

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async ({ tag, description }: UserEnquiryContextValues) => {
      try {
        const { data } = await api.enquiryContext.createEnquiryContext(
          tag,
          description,
        );
        onNewEnquiryContext(data);
        updatePreferences({ seenEnquiryContextFeature: true });
        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [modal, onNewEnquiryContext, popServerError, updatePreferences],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('addNewContextModalTitle')}
      {...rest}
    >
      <p>{t('addNewContextModalDescription')}</p>
      <UserEnquiryForm
        submitButtonText={t('saveTemplate')}
        onCancel={modal?.close}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default forwardRef(AddEnquiryContextModal);
